import { EnterAnimationStartEvent, TransitionStartEvent } from '@gob/events';

const selectors = {
  sections: 'section[data-theme]',
  header: '.site-header',
  banner: '[data-banner]'
}

class SectionObserver {
  #sections = null;
  #header = null;
  #banner = null;
  #sectionObserver = null;
  #bannerObserver = null;
  #direction = 'up';
  #lastY = 0;

  constructor() {
    this.handlePageChange = this.handlePageChange.bind(this);
    this.destroyObserver = this.destroyObserver.bind(this);

    document.addEventListener('DOMContentLoaded', () => {
      setTimeout(() => {
        this.init();
      }, 100)
    });
  }

  destroyObserver() {
    this.#sectionObserver?.disconnect();
    this.#bannerObserver?.disconnect();
    this.#sectionObserver = null;
    this.#bannerObserver = null;
    this.#sections = null;
    this.#banner = null;
    this.#header = null;
    this.#lastY = 0;
  }

  handlePageChange() {
    this.destroyObserver();

    requestAnimationFrame(() => {
      this.#setupObserver();
    });
  }

  #getTargetSection(target) {
    const index = this.#sections.findIndex((section) => section == target)
  
    if (index >= this.#sections.length - 1) {
      return this.#sections[index + 1];
    } else {
      return this.#sections[index + 1];
    }
  }

  #setScrollDirection() {
    const currentY = window.scrollY;
    
    this.#direction = currentY > this.#lastY ? 'down' : 'up';
    this.#lastY = currentY;
  }

  #updateTheme(target) {
    const theme = target.getAttribute('data-theme');
    
    this.#header.closest('site-header')?.setAttribute('data-theme', theme ?? 'light');
  }

  #updateBannerTheme(target) {
    const theme = target.getAttribute('data-theme');
    
    this.#banner.closest('announcement-banner')?.setAttribute('data-theme', theme ?? 'light');
  }

  #shouldUpdate(entry) {
    if (window.scrollY < 10) return false;
    
    if (this.#direction === 'down' && !entry.isIntersecting) return true;

    if (this.#direction === 'up' && entry.isIntersecting) return true;

    return false;
  }

  #setupObserver() {
    this.#sections = Array.from(document.querySelectorAll(selectors.sections));
    this.#header = document.querySelector(selectors.header);
    this.#banner = document.querySelector(selectors.banner);

    const firstSection = this.#sections[0];
    const firstTheme = firstSection?.getAttribute('data-theme');
    
    if (firstTheme) {
      document.body.classList.add('no-transition');
      this.#header?.closest('site-header')?.setAttribute('data-theme', firstTheme);
      this.#banner?.closest('announcement-banner')?.setAttribute('data-theme', firstTheme);

      requestAnimationFrame(() => {
        document.body.classList.remove('no-transition');
      });
    }
    
    this.#sectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        this.#setScrollDirection();

        const target = this.#direction === 'down' ? this.#getTargetSection(entry.target) : entry.target;
        
        if (!this.#shouldUpdate(entry)) return;
        
        this.#updateTheme(target);
      });
    }, {
      rootMargin: `${this.#header.offsetHeight * -1}px`,
      threshold: 0
    });

    if (this.#banner) {
      this.#bannerObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const target = this.#direction === 'down' ? this.#getTargetSection(entry.target) : entry.target;
          
          if (!this.#shouldUpdate(entry)) return;

          this.#updateBannerTheme(target);
        });
      }, {
        rootMargin: `${(this.#banner.offsetTop * -1)}px`,
        threshold: 0
      });
    }

    this.#sections.forEach((section) => {
      this.#sectionObserver.observe(section);
      this.#bannerObserver?.observe(section);
    });
  }

  init() {
    this.#setupObserver();

    window.addEventListener(EnterAnimationStartEvent.eventName, this.handlePageChange);
    window.addEventListener(TransitionStartEvent.eventName, this.destroyObserver);
  }
}

const sectionObserver = new SectionObserver();
export default sectionObserver;